import "./cleaner.css";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { STOCK_CATEGORIES, STOCK_CATEGORY_SHORT, STOCK_LEVEL_LABEL } from "../../shared/stock";
import type {
  MyStock,
  StockItemSummary,
  StockLevel,
  StockRequest,
  StockRequestBody,
  StockRequestStatus,
} from "../../shared/types";
import { Banner, ErrorBanner, Loading } from "../components/ui";
import { ApiError, post } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { useApi } from "../lib/useApi";
import { useSession } from "../session";

/** A small colour dot, so cleaners can match the bottle or bag. */
export function Swatch({ colour }: { colour: string | null }) {
  if (!colour) return <span className="cl-swatch none" aria-hidden="true" />;
  return <span className={`cl-swatch ${colour}`} aria-hidden="true" />;
}

const STATUS: Record<StockRequestStatus, [string, string]> = {
  open: ["info", "Sent to the office"],
  done: ["ok", "Sorted"],
  cancelled: ["", "Cancelled"],
};

function ItemRow({
  item,
  level,
  onChange,
}: {
  item: StockItemSummary;
  level: StockLevel | undefined;
  onChange: (l: StockLevel | undefined) => void;
}) {
  const options: StockLevel[] = item.kind === "collection" ? ["collect"] : ["low", "out"];
  const short: Record<StockLevel, string> = { low: "Low", out: "Run out", collect: "Needs collecting" };
  return (
    <li className={`cl-stock-item${level ? " picked" : ""}`}>
      <span className="cl-stock-name">
        <Swatch colour={item.colour} />
        {item.name}
      </span>
      <fieldset className="cl-seg" aria-label={item.name}>
        {options.map((o) => (
          <button
            key={o}
            type="button"
            className={`cl-seg-btn ${o}`}
            aria-pressed={level === o}
            onClick={() => onChange(level === o ? undefined : o)}
          >
            {short[o]}
          </button>
        ))}
      </fieldset>
    </li>
  );
}

function RequestCard({ r, onCancel }: { r: StockRequest; onCancel: (r: StockRequest) => void }) {
  const [kind, label] = STATUS[r.status];
  return (
    <li className="card flat cl-stock-req">
      <div className="row between cl-nowrap">
        <strong>{r.siteName}</strong>
        <span className={`badge ${kind}`}>{label}</span>
      </div>
      <div className="muted small">{fmtDateTime(r.createdAt)}</div>
      <ul className="cl-stock-lines">
        {r.lines.map((l) => (
          <li key={l.itemId} className="cl-stock-line">
            <Swatch colour={l.colour} /> {l.name}: {STOCK_LEVEL_LABEL[l.level].toLowerCase()}
          </li>
        ))}
      </ul>
      {r.note && <div className="muted small">“{r.note}”</div>}
      {r.status === "open" && (
        <button className="btn ghost sm" onClick={() => onCancel(r)}>
          Cancel request
        </button>
      )}
    </li>
  );
}

export function StockPage() {
  const { me, reload: reloadMe } = useSession();
  const stock = useApi<MyStock>("/stock");
  const [params, setParams] = useSearchParams();
  const fromFriday = params.get("friday") === "1";
  const openVisit = me?.openVisit ?? null;
  const openSiteId = openVisit?.siteId;
  // Assigned sites, plus the site they're clocked in at (they may have scanned in somewhere
  // they aren't assigned to; the server allows requests for it).
  const sites = useMemo(() => {
    const list = me?.sites ?? [];
    return openVisit && !list.some((s) => s.id === openVisit.siteId)
      ? [{ id: openVisit.siteId, name: openVisit.siteName, address: null, postcode: null }, ...list]
      : list;
  }, [me?.sites, openVisit]);
  const defaultSite = sites.find((s) => s.id === openSiteId)?.id ?? sites[0]?.id ?? "";
  const [siteId, setSiteId] = useState(defaultSite);
  const [picked, setPicked] = useState<Record<string, StockLevel>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // The site list arrives with the session; pick a default once it does.
  useEffect(() => {
    if (!siteId && defaultSite) setSiteId(defaultSite);
  }, [siteId, defaultSite]);

  const groups = useMemo(() => {
    const items = stock.data?.items ?? [];
    return STOCK_CATEGORIES.map((c) => ({ c, items: items.filter((i) => i.category === c) })).filter(
      (g) => g.items.length,
    );
  }, [stock.data]);

  if (!me) return null;
  if (!stock.data) {
    return (
      <div className="page-narrow cl-page">
        <h1>Stock</h1>
        {stock.error ? <ErrorBanner error={stock.error} onRetry={stock.reload} /> : <Loading />}
      </div>
    );
  }

  // Only items still on the list count (one may have been hidden since the page loaded).
  const activeIds = new Set(stock.data.items.map((i) => i.id));
  const livePicked = Object.entries(picked).filter(([itemId]) => activeIds.has(itemId));
  const count = livePicked.length;
  const checkDue = stock.data.checkDue;
  const finished = (msg: string) => {
    setDone(msg);
    setPicked({});
    setNote("");
    if (fromFriday) setParams({}, { replace: true });
    void stock.reload();
    void reloadMe();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  async function send() {
    if (!siteId || !count) return;
    setBusy(true);
    setError(null);
    try {
      const b: StockRequestBody = {
        siteId,
        lines: livePicked.map(([itemId, level]) => ({ itemId, level })),
        note: note.trim() || null,
        source: checkDue ? "friday" : "manual",
      };
      await post("/stock/requests", b);
      finished("Sent. The office has been told and will top you up.");
    } catch (e) {
      if (e instanceof ApiError && e.code === "unknown_item") void stock.reload();
      setError(e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function nothingNeeded() {
    setBusy(true);
    setError(null);
    try {
      await post("/stock/check", { siteId: siteId || null });
      finished("Thanks, noted that nothing is needed this week.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function cancel(r: StockRequest) {
    if (!window.confirm(`Cancel your request for ${r.siteName}?`)) return;
    try {
      await post(`/stock/requests/${r.id}/cancel`);
      setDone("Request cancelled.");
      void stock.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't cancel. Please try again.");
    }
  }

  return (
    <div className="page-narrow cl-page">
      <h1>Stock</h1>
      {done && (
        <Banner kind="ok">
          <div className="row between cl-nowrap">
            <span>{done}</span>
            <button className="btn ghost sm" onClick={() => setDone(null)} aria-label="Dismiss">
              ✕
            </button>
          </div>
        </Banner>
      )}

      {checkDue ? (
        <Banner kind="info">
          <strong>Friday stock check.</strong> Tap anything that's running low or has run out. If you have
          everything, tap <strong>Nothing needed</strong> at the bottom.
        </Banner>
      ) : (
        <p className="muted cl-tight">Running low on something? Tap it below and send it to the office.</p>
      )}

      {sites.length === 0 ? (
        <Banner kind="warn">You're not linked to any sites yet. Please ask the office.</Banner>
      ) : (
        <>
          {sites.length > 1 && (
            <label className="field">
              <span className="cl-block small">
                <strong>Which site?</strong>
              </span>
              <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {groups.map((g) => (
            <section key={g.c} className="card cl-stock-group">
              <h2>{STOCK_CATEGORY_SHORT[g.c]}</h2>
              <ul className="cl-stock-items">
                {g.items.map((i) => (
                  <ItemRow
                    key={i.id}
                    item={i}
                    level={picked[i.id]}
                    onChange={(l) =>
                      setPicked((p) => {
                        const next = { ...p };
                        if (l) next[i.id] = l;
                        else delete next[i.id];
                        return next;
                      })
                    }
                  />
                ))}
              </ul>
            </section>
          ))}

          <label className="field">
            <span className="cl-block small">
              <strong>Anything else?</strong> <span className="muted">(optional)</span>
            </span>
            <textarea
              value={note}
              maxLength={500}
              placeholder="e.g. The mop head is worn out"
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          {error && <div className="banner danger">{error}</div>}

          <button className="btn primary huge" disabled={busy || !count || !siteId} onClick={send}>
            {busy ? "Sending…" : count ? `Send to the office (${count})` : "Tap the items you need"}
          </button>
          {checkDue && (
            <button className="btn block" disabled={busy} onClick={nothingNeeded}>
              Nothing needed this week
            </button>
          )}
        </>
      )}

      {stock.data.requests.length > 0 && (
        <section className="cl-stock-history">
          <h2>Your requests</h2>
          <ul className="cl-visits">
            {stock.data.requests.map((r) => (
              <RequestCard key={r.id} r={r} onCancel={cancel} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
